import React, { memo, ReactElement, useCallback, useRef, useState, useEffect, useMemo } from "react";
import styles from "./AmountInputBox.scss";
import { useEffectOnce } from "@core-ui/hooks/UseEffectOnce";
import { TextField } from "@swiggy-private/dls-web-core/dist/components/TextField";
import { TextFieldTypes } from "@swiggy-private/dls-web-core/dist/components/TextField/types";
import { ASCII_CODES } from "./constants";
import { Container, RupeeContainer } from "./styles";

interface Props {
    amount?: string;
    onUpdateAmount: (amount?: string, trackEvent?: boolean) => void;
    reflectChangedAmountImmediately?: boolean;
    onInputFocus?: VoidFunction;
    onInputBlur?: VoidFunction;
    scrollIntoView?: boolean | number;
    inputFieldPlaceHolder: string;
    dataTestId: string;
}

let timeoutId: number;
const DEBOUNCE_TIMEOUT = 200;

const AMOUNT_MAX_LENGTH = 7;

const AmountInputBox = ({
    amount,
    onUpdateAmount,
    reflectChangedAmountImmediately,
    onInputFocus,
    onInputBlur,
    inputFieldPlaceHolder,
    dataTestId,
}: Props): ReactElement => {
    const [_amount, setAmount] = useState<string>(amount && Number(amount) > 0 ? amount : "");
    const inputFieldRef = useRef<HTMLInputElement>(null);

    const handleKeyEvent = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
        const { keyCode } = event;

        // on enter call the api
        if (keyCode === ASCII_CODES.ENTER) {
            inputFieldRef.current?.blur();
        }
    }, []);

    const inputAttrsUx4 = useMemo(() => {
        return {
            "data-testid": dataTestId,
            onKeyDown: handleKeyEvent,
        };
    }, [dataTestId, handleKeyEvent]);

    const setFocusOnInput = useCallback(() => {
        inputFieldRef.current?.focus();
    }, [inputFieldRef]);

    useEffectOnce(() => {
        if (Number(_amount) === 0) {
            setFocusOnInput();
        }
    });

    useEffect(() => {
        const value = Number(amount) > 0 ? amount : "";
        setAmount(value || "");
    }, [amount]);

    const onAmountChange = useCallback(
        (amount: string) => {
            setAmount(amount);

            if (reflectChangedAmountImmediately) {
                clearTimeout(timeoutId);
                timeoutId = window.setTimeout(() => {
                    onUpdateAmount(amount);
                }, DEBOUNCE_TIMEOUT);
            }
        },
        [reflectChangedAmountImmediately, onUpdateAmount],
    );

    const handleInputBlur = useCallback(() => {
        onUpdateAmount(_amount);
        onInputBlur?.();
    }, [onUpdateAmount, onInputBlur, _amount]);

    return (
        <Container>
            <RupeeContainer variant="V2.body1.neutral.bold" color="text_Highest_Emphasis">
                <div className={styles.rupee} />
            </RupeeContainer>
            <TextField
                ref={inputFieldRef}
                textFieldDetails={{
                    value: _amount,
                    onChange: onAmountChange,
                    onBlur: handleInputBlur,
                    onFocus: onInputFocus,
                    label: inputFieldPlaceHolder,
                    attrs: inputAttrsUx4,
                    type: TextFieldTypes.NUMBER,
                    name: "amount",
                    validFormat: "^[0-9]*$",
                    inputLimit: AMOUNT_MAX_LENGTH,
                }}
            />
        </Container>
    );
};

export default memo(AmountInputBox);
